import {
  type APIUser,
  can,
  ChannelType,
  type ChatState,
  PermissionFlags,
  roleColorHex,
  selectDisplayName,
  selectPresence,
  sortedRoles,
} from '@alternativamais/chat-core';
import { useEffect, useMemo, useState } from 'react';

import { Avatar } from '../../../componentes/Avatar';
import { Icone } from '../../../componentes/Icone';
import { useCliente } from '../../../chat/contexto';
import { type Gatilho, filtrarPorTermo } from '@alternativamais/chat-core';
import {
  buscarEmojis,
  carregarEmojis,
  type EmojiUnicode,
} from '../../../utilidades/emojis';
import { EmojiVisual } from '../SeletorEmoji';

export type Sugestao = {
  chave: string;
  /** O que entra no texto no lugar do gatilho. */
  inserir: string;
  /** Código para o envio, quando o texto inserido não basta (cargo com espaço). */
  codigo?: string;
} & (
  | {
      tipo: 'pessoa';
      usuario: APIUser;
      nome: string;
      status: string;
      cargo: { nome: string; cor: string } | null;
    }
  | { tipo: 'cargo'; nome: string; cor: string | null; membros: number }
  | { tipo: 'todos'; nome: string; descricao: string }
  | { tipo: 'canal'; nome: string; categoria: string | null; thread: boolean }
  | {
      tipo: 'emoji';
      emoji: { id: string | null; name: string; animated?: boolean };
      nome: string;
    }
);

export type Grupo = { titulo: string; itens: Sugestao[] };

const TEXTO = new Set<number>([
  ChannelType.GUILD_TEXT,
  ChannelType.GUILD_ANNOUNCEMENT,
  ChannelType.GUILD_FORUM,
  ChannelType.GUILD_MEDIA,
  ChannelType.PUBLIC_THREAD,
  ChannelType.ANNOUNCEMENT_THREAD,
]);

function pessoas(
  estado: ChatState,
  canalId: string,
  guildId: string | null,
  termo: string,
): Sugestao[] {
  const canal = estado.channels[canalId];
  const ids = guildId
    ? Object.keys(estado.members[guildId] ?? {})
    : (canal?.recipients ?? [])
        .map((pessoa) => pessoa.id)
        .concat(estado.me ? [estado.me.id] : []);
  const cargos = guildId
    ? sortedRoles(estado, guildId).filter(
        (cargo) => cargo.color && cargo.id !== guildId,
      )
    : [];
  const achadas = filtrarPorTermo(
    ids.filter((id) => estado.users[id]),
    termo,
    (id) => {
      const usuario = estado.users[id];
      return [
        selectDisplayName(estado, id, guildId),
        usuario.username,
        usuario.global_name ?? '',
      ];
    },
    8,
    (id) => (selectPresence(estado, id) === 'offline' ? 0 : 1),
  );
  return achadas.map((id) => {
    const usuario = estado.users[id];
    const membro = guildId ? estado.members[guildId]?.[id] : undefined;
    const cargo = membro
      ? cargos.find((item) => membro.roles.includes(item.id))
      : undefined;
    return {
      chave: `pessoa-${id}`,
      tipo: 'pessoa' as const,
      usuario,
      nome: selectDisplayName(estado, id, guildId),
      status: selectPresence(estado, id),
      cargo: cargo
        ? { nome: cargo.name, cor: roleColorHex(cargo.color) }
        : null,
      inserir: `@${usuario.username}`,
    };
  });
}

function cargosETodos(
  estado: ChatState,
  canalId: string,
  guildId: string | null,
  termo: string,
): Sugestao[] {
  if (!guildId) return [];
  const podeTodos = can(estado, canalId, PermissionFlags.MENTION_EVERYONE);
  const membros = Object.values(estado.members[guildId] ?? {});
  const cargos = filtrarPorTermo(
    sortedRoles(estado, guildId).filter(
      (cargo) =>
        cargo.id !== guildId &&
        !cargo.managed &&
        (cargo.mentionable || podeTodos),
    ),
    termo,
    (cargo) => [cargo.name],
    5,
  ).map((cargo) => ({
    chave: `cargo-${cargo.id}`,
    tipo: 'cargo' as const,
    nome: cargo.name,
    cor: cargo.color ? roleColorHex(cargo.color) : null,
    membros: membros.filter((membro) => membro.roles.includes(cargo.id)).length,
    inserir: `@${cargo.name}`,
    codigo: `<@&${cargo.id}>`,
  }));
  const todos = filtrarPorTermo(
    [
      { nome: '@everyone', descricao: 'Avisa todo mundo que vê o canal.' },
      { nome: '@here', descricao: 'Avisa quem está online e vê o canal.' },
    ],
    termo,
    (item) => [item.nome.slice(1)],
    2,
  ).map((item) => ({
    chave: item.nome,
    tipo: 'todos' as const,
    nome: item.nome,
    descricao: podeTodos
      ? item.descricao
      : 'Você não tem permissão para avisar todos.',
    inserir: item.nome,
  }));
  return [...cargos, ...todos];
}

function canais(
  estado: ChatState,
  guildId: string | null,
  termo: string,
): Sugestao[] {
  if (!guildId) return [];
  const ids = [
    ...(estado.guildChannelIds[guildId] ?? []),
    ...(estado.guildThreadIds[guildId] ?? []),
  ];
  const visiveis = ids
    .map((id) => estado.channels[id])
    .filter(
      (canal) =>
        canal &&
        TEXTO.has(canal.type) &&
        !canal.thread_metadata?.archived &&
        can(estado, canal.id, PermissionFlags.VIEW_CHANNEL),
    );
  return filtrarPorTermo(
    visiveis,
    termo,
    (canal) => [canal.name ?? ''],
    10,
  ).map((canal) => {
    const pai = canal.parent_id ? estado.channels[canal.parent_id] : undefined;
    const thread =
      canal.type === ChannelType.PUBLIC_THREAD ||
      canal.type === ChannelType.ANNOUNCEMENT_THREAD;
    return {
      chave: `canal-${canal.id}`,
      tipo: 'canal' as const,
      nome: canal.name ?? '',
      categoria: pai?.name ?? null,
      thread,
      inserir: `#${canal.name}`,
      codigo: `<#${canal.id}>`,
    };
  });
}

/** As sugestões do gatilho, agrupadas como no design (C4 e C43). */
export function useSugestoes(
  gatilho: Gatilho | null,
  canalId: string,
  guildId: string | null,
): Grupo[] {
  const cliente = useCliente();
  const [unicode, setUnicode] = useState<EmojiUnicode[] | null>(null);
  const tipo = gatilho?.tipo;
  useEffect(() => {
    if (tipo !== ':' || unicode) return;
    let vivo = true;
    void carregarEmojis().then((lista) => vivo && setUnicode(lista));
    return () => {
      vivo = false;
    };
  }, [tipo, unicode]);
  return useMemo(() => {
    if (!gatilho) return [];
    const estado = cliente.store.getState();
    if (gatilho.tipo === '@') {
      return [
        {
          titulo: 'Membros',
          itens: pessoas(estado, canalId, guildId, gatilho.termo),
        },
        {
          titulo: 'Cargos',
          itens: cargosETodos(estado, canalId, guildId, gatilho.termo),
        },
      ].filter((grupo) => grupo.itens.length);
    }
    if (gatilho.tipo === '#') {
      const itens = canais(estado, guildId, gatilho.termo);
      return itens.length ? [{ titulo: 'Canais', itens }] : [];
    }
    const guild = guildId ? estado.guilds[guildId] : undefined;
    const proprios = filtrarPorTermo(
      guild?.emojis ?? [],
      gatilho.termo,
      (emoji) => [emoji.name],
      5,
    ).map((emoji): Sugestao => ({
      chave: `emoji-${emoji.id}`,
      tipo: 'emoji',
      emoji: { id: emoji.id, name: emoji.name, animated: emoji.animated },
      nome: `:${emoji.name}:`,
      inserir: `:${emoji.name}:`,
    }));
    const comuns = buscarEmojis(
      unicode ?? [],
      gatilho.termo,
      10 - proprios.length,
    ).map((emoji): Sugestao => ({
      chave: `emoji-${emoji.unicode}`,
      tipo: 'emoji',
      emoji: { id: null, name: emoji.unicode },
      nome: `:${emoji.atalhos[0] ?? emoji.nome.replace(/\s+/g, '_')}:`,
      inserir: emoji.unicode,
    }));
    const itens = [...proprios, ...comuns];
    return itens.length ? [{ titulo: 'Emoji', itens }] : [];
  }, [cliente, gatilho, canalId, guildId, unicode]);
}

function Destaque({ texto, termo }: { texto: string; termo: string }) {
  const indice = termo ? texto.toLowerCase().indexOf(termo.toLowerCase()) : -1;
  if (indice < 0) return <>{texto}</>;
  return (
    <>
      {texto.slice(0, indice)}
      <span className="text-destaque-claro">
        {texto.slice(indice, indice + termo.length)}
      </span>
      {texto.slice(indice + termo.length)}
    </>
  );
}

/** A caixa de sugestões acima do compositor. */
export function Sugestoes({
  id,
  gatilho,
  grupos,
  selecionada,
  aoEscolher,
  aoPassar,
}: {
  id: string;
  gatilho: Gatilho;
  grupos: Grupo[];
  selecionada: number;
  aoEscolher: (sugestao: Sugestao) => void;
  aoPassar: (indice: number) => void;
}) {
  let indice = -1;
  const termo = gatilho.termo;
  const emoji = gatilho.tipo === ':';
  return (
    <div
      className={`absolute bottom-full left-0 z-10 mb-2 flex flex-col rounded-xl border border-borda bg-elevado p-2 shadow-[0_12px_32px_rgba(0,0,0,.45)] ${
        emoji ? 'w-[320px]' : 'w-[520px] max-w-full'
      }`}
    >
      <div
        id={id}
        role="listbox"
        aria-label={`Sugestões para ${gatilho.tipo}${termo}`}
        className="flex flex-col"
      >
        {grupos.map((grupo) => (
          <div
            key={grupo.titulo}
            role="group"
            aria-label={grupo.titulo}
            className="flex flex-col gap-0.5"
          >
            <div className="px-2 pt-1 pb-2 text-xs font-semibold tracking-[.04em] text-texto-3 uppercase">
              {grupo.titulo}
              {grupo === grupos[0] && termo && (
                <>
                  {' '}
                  que correspondem a{' '}
                  <span className="tracking-normal text-texto normal-case">
                    {gatilho.tipo}
                    {termo}
                  </span>
                </>
              )}
            </div>
            {grupo.itens.map((sugestao) => {
              indice++;
              const minha = indice;
              const ativa = minha === selecionada;
              return (
                <div
                  key={sugestao.chave}
                  id={`${id}-${minha}`}
                  role="option"
                  aria-selected={ativa}
                  onMouseDown={(evento) => {
                    evento.preventDefault();
                    aoEscolher(sugestao);
                  }}
                  onMouseEnter={() => aoPassar(minha)}
                  className={`flex cursor-pointer items-center gap-2.5 rounded-lg pr-2.5 pl-2 ${
                    emoji ? 'h-9' : 'h-10'
                  } ${ativa ? 'bg-ativo' : ''}`}
                >
                  <ConteudoSugestao sugestao={sugestao} termo={termo} />
                </div>
              );
            })}
          </div>
        ))}
      </div>
      <div className="mt-1.5 border-t border-borda px-2 pt-2 pb-0.5 text-xs text-texto-3">
        ↑↓ navegar · Enter ou Tab escolhe · Esc fecha
      </div>
    </div>
  );
}

function ConteudoSugestao({
  sugestao,
  termo,
}: {
  sugestao: Sugestao;
  termo: string;
}) {
  switch (sugestao.tipo) {
    case 'pessoa':
      return (
        <>
          <Avatar
            pessoa={sugestao.usuario}
            nome={sugestao.nome}
            tamanho={28}
            status={sugestao.status as 'online'}
            fundo="var(--cor-elevado)"
          />
          <span className="truncate text-[15px] font-semibold">
            <Destaque texto={sugestao.nome} termo={termo} />
          </span>
          <span className="truncate text-sm text-texto-3">
            {sugestao.usuario.username}
          </span>
          <span className="grow" />
          {sugestao.cargo && (
            <span className="flex shrink-0 items-center gap-1.5 text-xs font-semibold text-texto-2">
              <span
                className="size-2 rounded-full"
                style={{ background: sugestao.cargo.cor }}
              />
              {sugestao.cargo.nome}
            </span>
          )}
        </>
      );
    case 'cargo':
      return (
        <>
          <span className="flex w-7 shrink-0 justify-center">
            <span
              className="size-3 rounded-full"
              style={{ background: sugestao.cor ?? 'var(--cor-texto-3)' }}
            />
          </span>
          <span className="text-[15px] font-semibold">
            @<Destaque texto={sugestao.nome} termo={termo} />
          </span>
          <span className="text-[13px] text-texto-3">
            — {sugestao.membros} {sugestao.membros === 1 ? 'membro' : 'membros'}
          </span>
        </>
      );
    case 'todos':
      return (
        <>
          <span className="flex w-7 shrink-0 justify-center text-texto-3">
            <Icone nome="arroba" tamanho={18} />
          </span>
          <span className="text-[15px] font-semibold">{sugestao.nome}</span>
          <span className="truncate text-[13px] text-texto-3">
            {sugestao.descricao}
          </span>
        </>
      );
    case 'canal':
      return (
        <>
          <Icone
            nome={sugestao.thread ? 'thread' : 'hash'}
            tamanho={18}
            className="text-texto-3"
          />
          <span className="truncate text-[15px] font-semibold">
            <Destaque texto={sugestao.nome} termo={termo} />
          </span>
          <span className="grow" />
          {sugestao.categoria && (
            <span className="shrink-0 text-xs font-semibold text-texto-3 uppercase">
              {sugestao.categoria}
            </span>
          )}
        </>
      );
    case 'emoji':
      return (
        <>
          <span
            aria-hidden="true"
            className="flex w-6 shrink-0 justify-center text-xl"
          >
            <EmojiVisual emoji={sugestao.emoji} tamanho={22} />
          </span>
          <span className="truncate text-sm font-semibold">
            <Destaque texto={sugestao.nome} termo={termo} />
          </span>
        </>
      );
  }
}
